import { Injectable, OnDestroy } from '@angular/core';
import { isEmpty, isNil } from 'lodash';
import { BehaviorSubject, catchError, first, forkJoin, map, Observable, Subscription, switchMap, tap, throwError } from 'rxjs';
import { GenericAppFormIconProps } from '@app/shared/models/generic-app';
import { AzureBlobStorageService } from '@app/shared/services/api/azure-blob-storage.service';
import { StateService } from '@app/shared/services/state/state.service';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class ExterneAppsIconStateService extends StateService<ExterneAppsIconState> implements OnDestroy {
  protected externalAppIconStateSubscription$: Subscription | null = null;
  protected iconSubject$: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);

  public featureIcons$: Observable<[GenericAppFormIconProps[], GenericAppFormIconProps[]]> = this.select((state: ExterneAppsIconState) => [
    state.presets,
    state.uploads,
  ]);

  constructor(private azureBlobStorageService: AzureBlobStorageService) {
    super(initialExterneAppsIconState);
  }

  public ngOnDestroy(): void {
    this.externalAppIconStateSubscription$?.unsubscribe();
  }

  public clear(): void {
    this.setState(initialExterneAppsIconState);
  }

  private beginLoading(): void {
    this.setState({ ...this.state, loading: true, presets: [], uploads: [] });
  }

  public endLoading(): void {
    this.setState({ ...this.state, loading: false });
  }

  public dispatch(): void {
    this.clear();
    this.beginLoading();
    if (this.externalAppIconStateSubscription$) {
      this.ngOnDestroy();
    }
    this.externalAppIconStateSubscription$ = this.getIcons().subscribe(([presets, uploads]) => this.add(presets, uploads));
  }

  public addUpload(icon: GenericAppFormIconProps): void {
    let _uploads = !isNil(icon) ? [...this.state.uploads, icon] : this.state.uploads;
    this.setState({ ...this.state, uploads: _uploads });
  }

  public removeUpload(fileName: string): void {
    const stateUploads = [...this.state.uploads];
    const uploadsToSet = stateUploads.filter((uploads: GenericAppFormIconProps) => uploads.fileName !== fileName);
    this.setState({ ...this.state, uploads: uploadsToSet });
  }

  private add(presets: GenericAppFormIconProps[], uploads: GenericAppFormIconProps[]): void {
    let _presets = !isNil(presets) && !isEmpty(presets) ? [...this.state.presets, ...presets] : presets;
    let _uploads = !isNil(uploads) && !isEmpty(uploads) ? [...this.state.uploads, ...uploads] : uploads;

    this.setState({ ...this.state, presets: _presets, uploads: _uploads });
  }

  private getIcons(): Observable<[GenericAppFormIconProps[], GenericAppFormIconProps[]]> {
    return this.iconSubject$.pipe(
      first(),
      switchMap(() => this.getIconsFromBlob()),
      tap((_) => this.endLoading()),
      catchError((err, o: Observable<any>) => this.handleDefaultError(err, o))
    );
  }

  private getIconsFromBlob(): Observable<[GenericAppFormIconProps[], GenericAppFormIconProps[]]> {
    return forkJoin([
      this.azureBlobStorageService.getFiles('externe/presets').pipe(
        map((icons) =>
          icons.map((icon) => {
            return {
              fileName: icon.fileName,
              iconPath: `${environment.proxyApiURL}/api/icon/upload/${icon.fileName}`,
              fileType: icon.contentType,
              fileSize: icon.byteSize,
              isLoading: true,
            } as GenericAppFormIconProps;
          })
        )
      ),
      this.azureBlobStorageService.getFiles('externe/uploads').pipe(
        map((icons) =>
          icons.map((icon) => {
            return {
              fileName: icon.fileName,
              iconPath: `${environment.proxyApiURL}/api/icon/upload/${icon.fileName}`,
              fileType: icon.contentType,
              fileSize: icon.byteSize,
              isLoading: true,
              deletable: false,
            } as GenericAppFormIconProps;
          })
        )
      ),
    ]);
  }

  private handleDefaultError(error: any, o: Observable<any>): Observable<never> {
    this.setState({ ...this.state, loading: false, presets: [], uploads: [] });
    return throwError(() => error);
  }
}

export const initialExterneAppsIconState: ExterneAppsIconState = {
  loading: false,
  presets: [],
  uploads: [],
};

export interface ExterneAppsIconState {
  loading: boolean;
  presets: GenericAppFormIconProps[];
  uploads: GenericAppFormIconProps[];
}
